import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

const CACHE_KEY = 'categories'

// Tailwind color name → badge + dot class sets
const COLOR_CLASSES = {
    amber:   { badge: 'bg-amber-100 text-amber-700 border-amber-200',     dot: 'bg-amber-400',   bg: 'bg-amber-50' },
    blue:    { badge: 'bg-blue-100 text-blue-700 border-blue-200',        dot: 'bg-blue-400',    bg: 'bg-blue-50' },
    emerald: { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400', bg: 'bg-emerald-50' },
    violet:  { badge: 'bg-violet-100 text-violet-700 border-violet-200',  dot: 'bg-violet-400',  bg: 'bg-violet-50' },
    rose:    { badge: 'bg-rose-100 text-rose-700 border-rose-200',        dot: 'bg-rose-400',    bg: 'bg-rose-50' },
    sky:     { badge: 'bg-sky-100 text-sky-700 border-sky-200',           dot: 'bg-sky-400',     bg: 'bg-sky-50' },
    orange:  { badge: 'bg-orange-100 text-orange-700 border-orange-200',  dot: 'bg-orange-400',  bg: 'bg-orange-50' },
    teal:    { badge: 'bg-teal-100 text-teal-700 border-teal-200',        dot: 'bg-teal-400',    bg: 'bg-teal-50' },
    gray:    { badge: 'bg-gray-100 text-gray-600 border-gray-200',        dot: 'bg-gray-400',    bg: 'bg-gray-50' },
}

// Fallback categories when DB is unavailable
const FALLBACK_CATEGORIES = [
    { id: 'concrete', name: 'CONCRETE', color: 'amber' },
    { id: 'fence',    name: 'FENCE',    color: 'blue' },
    { id: 'pergola',  name: 'PERGOLA',  color: 'emerald' },
]

/**
 * useCategories — fetches categories from Supabase.
 * Falls back to the 3 hardcoded categories on error.
 *
 * @returns {{ categories, loading, error, createCategory, reload }}
 */
export function useCategories() {
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES)
    const [loading,    setLoading]    = useState(true)
    const [error,      setError]      = useState(null)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)

        const cached = await loadReferenceCache(CACHE_KEY, null).catch(() => null)
        if (cached) {
            setCategories(cached.data)
            if (!silent) setLoading(false)
        }

        const { data, error: err } = await supabase
            .from('categories')
            .select('*')
            .order('name', { ascending: true })

        if (id !== loadIdRef.current) return

        if (err) {
            console.error('[categories] fetch error:', err.message)
            setError(err.message)
            if (!cached) setCategories(FALLBACK_CATEGORIES)
        } else {
            const next = data?.length ? data : FALLBACK_CATEGORIES
            setCategories(next)
            await saveReferenceCache(CACHE_KEY, next, null).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    /** Insert a new category row. */
    const createCategory = useCallback(async ({ name, color = 'gray' }) => {
        const trimmed = name.trim().toUpperCase()
        if (!trimmed) throw new Error('name is required')
        const { data, error: err } = await supabase
            .from('categories')
            .insert({ name: trimmed, color })
            .select()
            .single()
        if (err) {
            console.error('[categories] insert error:', err.message)
            throw new Error(err.message)
        }
        setCategories(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [])

    /** Rename a category and/or change its color. */
    const updateCategory = useCallback(async (id, patch) => {
        if (patch.name) patch.name = patch.name.trim().toUpperCase()
        const { data, error: err } = await supabase
            .from('categories')
            .update(patch)
            .eq('id', id)
            .select()
            .single()
        if (err) {
            console.error('[categories] update error:', err.message)
            throw new Error(err.message)
        }
        setCategories(prev => prev.map(c => c.id === id ? data : c).sort((a, b) => a.name.localeCompare(b.name)))
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [])

    /** Delete a category by id. Caller is responsible for reassigning orphaned catalog items first. */
    const deleteCategory = useCallback(async (id) => {
        setCategories(prev => prev.filter(c => c.id !== id))
        const { error: err } = await supabase
            .from('categories')
            .delete()
            .eq('id', id)
        if (err) {
            console.error('[categories] delete error:', err.message)
            throw new Error(err.message)
        }
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
    }, [])

    return { categories, loading, error, createCategory, updateCategory, deleteCategory, reload: load, silentReload }
}

/**
 * Get Tailwind class set for a category name.
 * @param {Array} categories — from useCategories()
 * @param {string} catName   — e.g. 'CONCRETE'
 * @returns {{ badge, dot, bg }}
 */
export function getCatColor(categories, catName) {
    const cat = categories.find(c => c.name === catName)
    return COLOR_CLASSES[cat?.color] || COLOR_CLASSES.gray
}

export { FALLBACK_CATEGORIES, COLOR_CLASSES }
